define((require) => {
    "use strict";

    const Configuration = require("shared/services/Configuration");
    const _             = require("underscore");
    const $             = require("jquery");
    const Window        = require("shared/services/Window");
    const {Logger}      = require("shared/util/Logger");
    const channelBridge = require("shared/ui/viewer/ChannelBridge");

    const URLS = {};
    let location;

    // valid url simply: https://.., http://.., //.., or /.. (relative)
    const VALID_REGEX = /^https:\/\/|^http:\/\/|^\//; // none of these use /g, so we do not care about lastIndex!
    const EMBED_REGEX = /(?:viewer.html(?:\?([a-z]+)=[^#]+)?#|^#)(group\/)?([0-9a-f]{24})(\/\d+)?$/; // allow short hand notation like "#id", "#group/id", and "#group/id/scene" as well

    /**
     * Contains url utility functions.
     *
     * @class UrlUtil
     */
    const scope = {

        /**
         * Returns the origin of the currently-loaded page. <p>
         *
         * If embedded via channels, this will be the actual client url; when viewed via a direct link, this will be
         * the viewer url. <p>
         *
         * @returns {String} The origin of the currently-loaded page; never falsey.
         */
        origin() {
            const location = window.location;
            return location.origin || `${location.protocol}//${location.host}`;
        },

        /**
         * Returns the base url of the client, i.e. origin + path name, excluding a trailing slash. <p>
         *
         * If embedded via channels, this will be the actual client base url; when viewed via a direct link,
         * this will be the viewer base url. <p>
         *
         * @returns {String} The base URL of the client; never falsey.
         */
        baseUrl() {
            const url = scope.origin() + window.location.pathname;

            if (url.substr(-1) === "/") {
                return url.substr(0, url.length - 1);
            }

            return url;
        },

        /**
         * Gets an url descriptor for the specified url. <p>
         *
         * If the url is determined to link to an Experience or Group, the returned meta data will identify said
         * content via the <tt>id</tt> property and the <tt>type</tt> property will be either "experience" or
         * "group", and "valid" will be true. <p>
         *
         * If the embed url does not represent an Experience or Group, the <tt>type</tt> property will be set
         * to "url" and the <tt>valid</tt> property will describe if the url is determined to be valid or not.
         * The validation is simple, only checking for "https://", "http://", "//", or "/" prefixes. The
         * "sameOrigin" property will be true if the url has the same domain as the top most reachable window,
         * e.g. the client embedding an Experience, including if "url" is relative. <p>
         *
         * The returned descriptor will also successfully match an embed url for an Experience or Group generated
         * via <tt>getEmbedURL(descriptor, embedType, scene)</tt>.
         *
         * @param   {String} url The url to test for embedding; can be falsey.
         * @returns {{id:String, scene:Number, url:String, type:String, embedType:String, sameOrigin:Boolean, valid:Boolean}} A descriptor
         *          describing the embed url; never falsey, but <tt>id</tt> property will only be set if the url is
         *          determined to link to an Experience or Group.
         *
         * @see     #getEmbedURL
         */
        getUrlDescriptor(url) {
            url = url || "";
            const match = EMBED_REGEX.exec(url);
            const descriptor = {type: "url", url};
            const topLocation = Window.topWindow().location;

            // (Recursively) embedded Experience or Group...
            if (match) {
                descriptor.id        = match[3];
                descriptor.type      = match[2] ? "group" : "experience"; // match[1] undefined on experience match
                descriptor.scene     = match[4] ? parseInt(match[4].substr(1), 10) || 1 : 1;  // matched "/" + \d+ or undefined, so drop "/" on match
                descriptor.embedType = match[1] || "none"; // e.g. "lightbox" or "embedded"
                descriptor.valid     = true; // TODO: consider valid() checking for pushed content in Editor only, requires async method
                // Since we know the viewer base url is always a zmags domain, this will be false when embedded
                // successfully via channels, e.g. client domain vs Zmags domain, or true when embedded via a
                // direct url, perhaps because of a fallback direct url if the embedding via channels fails. It will
                // also be false in the editor for a top level Viewer embedding an Experience, as the Editor does
                // things differently... :/
                descriptor.sameOrigin = scope.sameOrigin(Configuration.viewerBaseUrl, topLocation);

            // Some other url *non* Experience/Group url...
            } else {
                descriptor.valid = VALID_REGEX.test(url);
                // Same origin on the url, e.g. a link to a client specific resource when embedded via channels; must
                // be embedded, or the origin with be that of the viewer. The use case for a url descriptor ensures
                // us that a relative url will always be opened relative to the top level window, so any relative
                // url is by default on the same origin...
                descriptor.sameOrigin = scope.sameOrigin(url, topLocation);
            }

            return descriptor;
        },

        /**
         * Gets the URL for to use for as the "src" for the iframe eventually embedded in the dom, either in the
         * Viewer or Editor context. <p>
         *
         * If the url is invalid, "about:blank" will be returned as nothing can be displayed for such an embedded
         * iframe.
         *
         * @param   {{id:String, scene:Number, type:String, url:String, embedType:String, sameOrigin:Boolean, valid:Boolean}} descriptor
         *          The url descriptor to use, earlier obtained via <tt>getUrlDescriptor</tt>; cannot be falsey.
         *
         * @param   {String} [embedType] The embed type, e.g. "lightbox" or "embedded"; can be falsey to generate a
         *                               "raw embed url", i.e. ignored. NOTE: new values require Akamai to be
         *                               configured accordingly!
         *
         * @param   {Number} [scene]     The current scene in this Experience/Viewer to qualify the url, not the
         *                               scene specified by the url descriptor; can be falsey when generating a
         *                               "raw embed url". This is also used to qualify log names in embedded Viewers.
         *
         * @returns {String} The embed URL for the for an iframe to eventually embed; never falsey.
         *
         * @see     #getUrlDescriptor
         */
        getEmbedURL(descriptor, embedType, scene) {
            let url;

            // For embedded Experiences and Group, we manually construct the url to use on embedding, regardless of
            // what the user actually entered...
            if (descriptor.type !== "url") {
                // IMPORTANT:
                // To avoid cross domain issues in the Editor on preview/quickview, the Viewer always live on
                // c.zmags.com and the editor on creator.zmags.com. Trying to visit "creator.zmags.com/viewer.html?.."
                // will redirect to "c.zmags.com/viewer.html?..". The Viewer url is configured, and we must thus
                // always use its configured url, regardless what the user has entered for the embed url (descriptor).
                // So, all recursively nested Experiences will use the same configured domain, e.g. "c.zmags.com" in
                // prod, but we must still remember the client protocol and optional path (to the viewer, in branch
                // environment)...

                // Generate raw Viewer url, always ending with "/viewer.html" and using the client protocol, then
                // we add relevant query parameters and hash value, which we control...
                url = scope.getViewerUrl();

                if (embedType) {
                    url += `?${embedType}=${descriptor.id}:${scene}&zmagsLog=${Logger.level()}`;
                }

                url += "#";

                // Finally add (qualified) id...
                if (descriptor.type === "group") {
                    url += "group/";
                }
                url += descriptor.id;

                // Scene 1 is default, so do not bother with it...
                if (descriptor.scene !== 1) {
                    url += `/${descriptor.scene}`;
                }

            // Note that "url" can be relative, effectively against the top most reachable window, i.e.
            // the client page when embedded via channels, or the viewer url when via a direct url...
            } else {
                url = descriptor.valid ? descriptor.url : "about:blank";
            }

            return url;
        },

        /**
         * Returns true if the specified urls have the same origin, false otherwise.
         *
         * @param   {String} a The first url to test; cannot be null/undefined.
         * @param   {String} b The second url to test; cannot be null/undefined.
         * @returns {Boolean} True if the same origin, false otherwise.
         */
        sameOrigin(a, b) {
            return scope.location(a).origin === scope.location(b).origin;
        },

        /**
         * Returns true if the specified urls have the same hostname, false otherwise.
         *
         * @param   {String} a The first url to test; cannot be null/undefined.
         * @param   {String} b The second url to test; cannot be null/undefined.
         * @returns {Boolean} True if the same Hostname, false otherwise.
         */
        sameHostname(a, b) {
            return !!a && !!b && scope.location(a).hostname === scope.location(b).hostname;
        },

        /**
         * Prepends "http://" to the given URL, if it doesn't already contain a valid protocol prefix.
         *
         * @param {String}  url The url.
         * @param {Boolean} [forceHttps] should force to use https instead of http; optional.
         * @returns {String} The url, prepended with the protocol.
         */
        prependHTTP(url, forceHttps) {
            const HTTP_PREFIX = "http://";
            const HTTPS_PREFIX = "https://";
            const DOUBLE_SLASH_PREFIX = "//";
            const lowercaseUrl = url.toLowerCase();

            if (lowercaseUrl.indexOf(HTTP_PREFIX) !== 0 && lowercaseUrl.indexOf(HTTPS_PREFIX) !== 0 && lowercaseUrl.indexOf(DOUBLE_SLASH_PREFIX) !== 0) {
                return (forceHttps ? HTTPS_PREFIX : HTTP_PREFIX) + url;
            }

            return url;
        },

        /**
         * Prepends "http://" or the "https://" to the given URL, if it doesn't already contain a valid protocol prefix.
         * Prepended protocol depends on the current page protocol, for prevent mixed content.
         *
         * @param {String}  url The url.
         * @returns {String} The url, prepended with the protocol.
         */
        prependProtocol(url) {
            const HTTP_PREFIX = "http://";
            const HTTPS_PREFIX = "https://";
            const DOUBLE_SLASH_PREFIX = "//";
            const lowercaseUrl = url.toLowerCase();
            const currentProtocol = window.location.protocol;

            if (lowercaseUrl.indexOf(HTTP_PREFIX) !== 0 && lowercaseUrl.indexOf(HTTPS_PREFIX) !== 0) {
                if (lowercaseUrl.indexOf(DOUBLE_SLASH_PREFIX) === 0) {
                    return `${currentProtocol}${url}`;
                }
                return `${currentProtocol}${DOUBLE_SLASH_PREFIX}${url}`;
            }

            return url;
        },

        /**
         * Returns a "mock" location object for the supplied url, defaulting to the current location of the top
         * most reachable window, or a specific property, e.g. "hostname", from said  mock location. <p>
         *
         * Note: if <tt>url</tt> does not have a protocol, i.e. http:, https:, or //, <tt>url</tt> will be treated
         * as a relative url to the top most reachable window location!
         *
         * @param   {String|*} [url]  The url to return the location object for; will default to the current location
         *                            of the top most reachable window.
         * @param   {String}   [part] The specific part requested, if any; can be null/undefined.
         *
         * @returns {{protocol:String, host:String, hostname:String, href:String, port:String, origin:String, pathname:String, last:String, hash:String, search:String, query:{}}|String}
         *          A "mock" location object describing <tt>url</tt>, or the part from it as specified by
         *          <tt>part</tt>.
         */
        location(url, part) {
            const win = Window.topWindow();
            url = url || win.location.href;
            if (!location) {
                location = win.document.createElement("a");
            }

            // Will calculate fields for us, like "host", "pathname", etc...
            location.href = url.href || url;

            // IE has trouble with // urls like this, and we cannot simply set the href of "window" or "document"... :/
            const protocol = location.protocol || win.location.protocol;

            const parts = {
                protocol,
                host:     location.host,
                hostname: location.hostname,
                href:     location.href, // correct in IE even when protocol agnostic!
                port:     location.port,
                origin:   location.origin || `${protocol}//${location.host}`,
                pathname: location.pathname, // never empty and always prefixed with "/"
                last:     location.pathname.split("/").pop(),
                hash:     location.hash,
                search:   location.search,
                query:    {}
            };

            // Parse search part into query parameters when needed...
            if ((!part || part === "query") && parts.search) {
                const query = parts.search.substr(1).split("&"); // drop "?" before params
                let pair;
                let i;

                for (i = 0; i < query.length; i++) {
                    pair = query[i].split("=");
                    parts.query[pair[0]] = pair[1];
                }
            }

            return part ? parts[part] : parts;
        },

        /**
         * Appends a query parameter to the given url.
         *
         * @param {String} url The url to append a query parameter to.
         * @param {String} param The query parameter.
         * @returns {String} The url with a parameter appended.
         */
        appendParam(url, param) {
            let firstPart;
            let lastPart;

            if (url.indexOf("?") !== -1) {
                firstPart = url.split("?")[0];
                lastPart = url.split("?")[1];

                return `${firstPart}?${param}&${lastPart}`;
            } else if (url.indexOf("#") !== -1) {
                firstPart = url.split("#")[0];
                lastPart = url.split("#")[1];

                return `${firstPart}?${param}#${lastPart}`;
            }

            return `${url}?${param}`;
        },

        /**
         * Create a serialized representation of the specified object (or array) and
         * appends it to the specified URL, returning the new URL string.
         *
         * @param  {String} url         The base URL.
         * @param  {Object|Array} paramObject An array or object to serialize.
         * @return {String}             The new URL string.
         */
        appendParamObject(url, paramObject) {
            const paramString = $.param(paramObject);
            const delimiter = (url.indexOf("?") !== -1) ? "&" : "?";

            return url + delimiter + paramString;
        },

        /**
         * Returns the absolute URL for a given path or URL.
         * An empty path ("") will return "".
         *
         * @param   {String} path The path or URL to get as an absolute URL.
         * @returns {String}      An absolute URL for the path.
         */
        getAbsoluteViewerApiUrl(path) {
            if (this.isAbsoluteUrl(path) || path === "") {
                return path;
            }

            // Fix of closed LB images url to S3 source
            if (this.isImageUrl(path) && Configuration.branchImagesBaseUrl) {
                return `${Configuration.branchImagesBaseUrl}/${path}`;
            }

            return `${this.getStaticDomain(path)}/${path}`;
        },

        /**
         * Returns the (configured) protocol agnostic Viewer url, respecting an optional intermediate path, but
         * with out any query parameters or hash value. That is, the url will always end with "/viewer.html".
         *
         * @returns {String} (configured) protocol agnostic Viewer url; never falsey.
         */
        getViewerUrl() {
            // This relies on the fact that config urls running on both http and https are defined as protocol
            // agnostic, e.g. starting with "//" and not, e.g. "http://". We know the "url.href" below does not
            // include query parameters or a hash value since constructed based on the path name...
            let url = Configuration.viewerBaseUrl;

            // This can still be called while in bridge mode in case the embedding via channels fails and in such
            // cases we want to be able to generate a useful viewer url...
            if (!channelBridge()) {
                url += window.location.pathname;
            }

            url = scope.location(url); // path name always starts with "/", even if empty otherwise

            // Can already point to the viewer in case of embedded Experiences...
            if (url.last === "viewer.html") { // viewer
                return url.href;

            // Editor...
            } else if (url.last === "index.html") { // editor
                url.href = url.href.substring(0, url.href.length - 10); // drop "index.html"
            }

            // Always append "viewer.html"; at this point, "url.href" will always end with a "/"...
            return `${url.href}viewer.html`;
        },


        _getAbsoluteContentUrl(postfix) {
            const viewerUrl = `${scope.getViewerUrl()}#${postfix}`;
            const urlPrefix = (viewerUrl.indexOf("http") >= 0 ? "" : "http:");

            return urlPrefix + viewerUrl;
        },

        /**
         * Returns the absolute URL for a given experience.
         *
         * @param   {String} experienceId The id of an experience.
         * @returns {String}              An absolute URL for the experience.
         */
        getAbsoluteExperienceUrl(experienceId) {
            return `${process.env.APP_CONFIG.cncViewLiteBaseUrl}${experienceId}`;
        },

        /**
         * Returns the absolute URL for a given group.
         *
         * @param   {String} groupId The id of a group.
         * @returns {String}         An absolute URL for the group.
         */
        getAbsoluteGroupUrl(groupId) {
            return this._getAbsoluteContentUrl(`group/${groupId}`);
        },

        /**
         * Tests if the URL starts with "http" or "//".
         *
         * @param {String} url The URL to test.
         */
        isAbsoluteUrl(url) {
            return url.indexOf("http") === 0 || url.indexOf("//") === 0;
        },

        /**
         * Tests if the URL contains image path 'assets/images'
         *
         * @param {String} url The URL to test.
         */
        isImageUrl(url) {
            return url.indexOf("assets/images") > -1;
        },

        /**
         * Given a URL or path, returns the file name with extension or <tt>null</tt> if none.
         *
         * @param  {String} url URL or path pointing to a specific file.
         * @return {String}     The name with extension, if any. E.g. 'foo/image.jpg' will return 'image.jpg'.
         */
        getFileNameWithExtension(url) {
            if (!url) {
                return null;
            }
            return url.split("/").pop().replace(/[?|#].+/, "");
        },

        /**
         * Given a URL or path, returns the file extension or <tt>null</tt> if none.
         *
         * @param  {String} url URL or path pointing to a specific file.
         * @return {String}     The file extension, if any. E.g. 'foo/image.jpg' will return 'jpg'.
         */
        getFileExtension(url) {
            // Also remove query string if any
            const fileName = url.split("/").pop().replace(/[?|#].+/, "");
            // There may be multiple dots in the file name, we need to match the last dot
            const match = fileName.match(/\.(?=[^.]*$)([^?]+)/);

            return match ? match[1] : null;
        },

        /**
         * Get static domain for an asset.
         *
         * We have two static domains and we will distribute them evenly for assets. All the requested asset URLs
         * are stored in the URLs map. We use the total number of assets in the map to compute the oddness of current
         * assets, if odd return one domain, else return another.
         *
         * @param {String} url   The relative url of an asset
         */
        getStaticDomain(url) {
            let domain = URLS[url];
            const isOdd = _.keys(URLS).length % 2;

            if (!domain) {
                domain = isOdd ? Configuration.viewerAliasApiBaseUrl : Configuration.viewerApiBaseUrl;
                URLS[url] = domain;
            }

            return domain;
        },

        /**
         * Safe concatenation of two or more pieces of url.<p>
         * e.g:
         *     concat('zmags.salesforcecommerce.com/','/dw/shop/v15_1/')
         *     concat('zmags.salesforcecommerce.com','/dw/shop/v15_1/')
         *     concat('zmags.salesforcecommerce.com/','dw/shop/v15_1/')
         *     concat('zmags.salesforcecommerce.com','dw/shop/','v15_1/')
         *
         * @returns {String}
         */
        concat(...args) {
            let begin = "";
            let i = 0;
            let end;
            let slash;

            while (i < arguments.length) {
                begin += (args[i++] || "");

                end = i < arguments.length ? args[i++] || "" : "";
                // if begin and end are defined
                slash = (begin && end) ? "/" : "";

                while (begin[begin.length - 1] === "/") {
                    begin = begin.substring(0, begin.length - 1);
                }

                while (end[0] === "/") {
                    end = end.substring(1);
                }

                begin = begin + slash + end;
            }

            return begin;
        }
    };

    return scope;
});



// WEBPACK FOOTER //
// ./src/shared/util/UrlUtil.js